import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  FileText,
  DollarSign,
  Clock,
  CheckCircle2,
  MessageCircle,
  Upload,
  ArrowRight,
  AlertTriangle,
  CreditCard,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export default function ClientDashboard() {
  const { portalInfo } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Display name for the firm/business that owns this portal. Falls back
  // gracefully when the workspace name isn't loaded yet.
  const firmName = portalInfo?.workspace_name || data?.workspace_name || 'them';

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/client-portal/dashboard`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          Welcome back{data?.contact?.name ? `, ${data.contact.name}` : ''}
        </h1>
        <p className="text-muted-foreground mt-1">
          {data?.workspace_name ? `Your account with ${data.workspace_name}` : 'Your client portal overview'}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold" data-testid="stat-outstanding-amount">{formatCurrency(data?.total_outstanding)}</p>
                <p className="text-xs text-muted-foreground">{data?.invoices_outstanding || 0} invoice{data?.invoices_outstanding !== 1 ? 's' : ''}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold" data-testid="stat-paid-amount">{formatCurrency(data?.total_paid)}</p>
                <p className="text-xs text-muted-foreground">{data?.invoices_paid || 0} invoice{data?.invoices_paid !== 1 ? 's' : ''}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Messages</p>
                <p className="text-2xl font-bold" data-testid="stat-unread-messages">{data?.unread_messages || 0}</p>
                <p className="text-xs text-muted-foreground">unread</p>
              </div>
              <MessageCircle className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Documents</p>
                <p className="text-2xl font-bold" data-testid="stat-documents">{data?.documents_count || 0}</p>
                <p className="text-xs text-muted-foreground">uploaded</p>
              </div>
              <Upload className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/portal/invoices" data-testid="quick-action-invoices">
          <Card className="hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors cursor-pointer h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">View Invoices</p>
                <p className="text-xs text-muted-foreground">See all your invoices and make payments</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/portal/documents" data-testid="quick-action-documents">
          <Card className="hover:border-blue-300 dark:hover:border-blue-700 transition-colors cursor-pointer h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Upload Documents</p>
                <p className="text-xs text-muted-foreground">Send receipts and documents to {firmName}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/portal/messages" data-testid="quick-action-messages">
          <Card className="hover:border-purple-300 dark:hover:border-purple-700 transition-colors cursor-pointer h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Message {firmName}</p>
                <p className="text-xs text-muted-foreground">Ask questions or share updates</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Invoices */}
      {data?.recent_invoices?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Invoices</CardTitle>
              <Link to="/portal/invoices">
                <Button variant="ghost" size="sm" className="text-xs">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_invoices.map(inv => {
                const isPayable =
                  inv.payment_token &&
                  inv.status !== 'paid' &&
                  inv.status !== 'draft' &&
                  inv.status !== 'void';
                return (
                  <div key={inv.invoice_id} className="flex items-center justify-between py-2 border-b last:border-0 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">Invoice #{inv.invoice_no}</p>
                        <p className="text-xs text-muted-foreground">{inv.invoice_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm font-mono font-medium">{formatCurrency(inv.total)}</span>
                      <Badge className={cn(
                        "text-[10px]",
                        inv.status === 'paid' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                        inv.status === 'posted' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                        inv.status === 'overdue' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                        inv.status === 'partial' && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                      )}>
                        {inv.status}
                      </Badge>
                      {isPayable && (
                        <Button
                          size="sm"
                          onClick={() => window.open(`/pay/${inv.payment_token}`, '_blank', 'noopener,noreferrer')}
                          className="h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          data-testid={`dashboard-pay-${inv.invoice_no}`}
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          Pay
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No data state */}
      {!data?.contact && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h3 className="font-semibold mb-1">Portal Setup Pending</h3>
            <p className="text-sm text-muted-foreground">Your portal is being set up. Check back soon or contact them directly.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
